import React, { useCallback, useEffect, useState } from 'react';
import { getPrintOrder, listPrintOrders, retryPrintOrder } from '../../services/printOrdersAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import { printRunProgressMessage, usePrintOrderRun } from '../../hooks/usePrintOrderRun';
import type { PrintOrderListRowResponse, PrintOrderWithSheetsResponse } from '../../schemas/apiResponse.schemas';
import { PRINT_FAILURE_LABELS } from '@/shared/types/printOrder.types';
import { DEFAULT_PRINTER_ID } from '@/shared/types/printer.types';
import { useResumePrintEventScan } from '../../hooks/useResumePrintEventScan';
import PrintEventHandlingModal from './PrintEventHandlingModal';
import './PrintBoxLabel.css';
import './PrintOrdersList.css';

const PAGE_SIZE = 20;

/** Local M/D plus time, e.g. "9/4, 2:03 PM". */
function formatPrintDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  const monthDay = `${parsed.getMonth() + 1}/${parsed.getDate()}`;
  const time = parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${monthDay}, ${time}`;
}

function displayUser(row: PrintOrderListRowResponse): string {
  return row.createdByName || row.createdBy || 'Unknown';
}

function PrintOrdersList(): React.ReactElement {
  const [entries, setEntries] = useState<PrintOrderListRowResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [message, setMessage] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [retryTarget, setRetryTarget] = useState<PrintOrderListRowResponse | null>(null);
  const [handlingJob, setHandlingJob] = useState<PrintOrderWithSheetsResponse | null>(null);
  const [resumedScannedPrefix, setResumedScannedPrefix] = useState<string | null>(null);
  const [openingJobId, setOpeningJobId] = useState<string | null>(null);
  const printEventResume = useResumePrintEventScan();

  useEffect(() => {
    if (!printEventResume.job) return;
    setHandlingJob(printEventResume.job);
    setResumedScannedPrefix(printEventResume.scannedPrefix);
  }, [printEventResume.job, printEventResume.scannedPrefix]);

  const { runOrder, isRunning } = usePrintOrderRun();

  const showMessage = (text: string, isError: boolean, clearAfterMs = 4000): void => {
    setIsErrorMessage(isError);
    setMessage(text);
    setTimeout(() => {
      setMessage('');
      setIsErrorMessage(false);
    }, clearAfterMs);
  };

  const loadPage = useCallback(async (nextOffset: number, append: boolean): Promise<void> => {
    setLoading(true);
    try {
      const result = await listPrintOrders(nextOffset, PAGE_SIZE);
      setEntries((prev) => (append ? [...prev, ...result.entries] : result.entries));
      setHasMore(result.hasMore);
      setOffset(nextOffset + result.entries.length);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage(0, false);
  }, [loadPage]);

  const handleRetry = async (row: PrintOrderListRowResponse): Promise<void> => {
    setRetryTarget(null);
    setIsErrorMessage(false);
    setMessage(`Reprinting ${row.unprintedLabels} label${row.unprintedLabels !== 1 ? 's' : ''}...`);

    try {
      const retry = await retryPrintOrder(row.id);
      const result = await runOrder(retry, {
        printerId: DEFAULT_PRINTER_ID,
        onProgress: (progress) => {
          setMessage(printRunProgressMessage(progress));
        },
      });
      showMessage(result.message, !result.completed, result.completed ? 4000 : 10000);
    } catch (error: unknown) {
      showMessage(getApiErrorMessage(error, 'Failed to start the reprint.'), true, 10000);
    } finally {
      await loadPage(0, false);
    }
  };

  const openPrintEventHandling = async (row: PrintOrderListRowResponse): Promise<void> => {
    if (openingJobId || isRunning) return;
    setOpeningJobId(row.id);
    setIsErrorMessage(false);
    setMessage('Loading print job...');
    try {
      const job = await getPrintOrder(row.id);
      if (!job) {
        showMessage('Could not load that print job.', true);
        return;
      }
      setMessage('');
      setResumedScannedPrefix(null);
      setHandlingJob(job);
    } catch (error: unknown) {
      showMessage(getApiErrorMessage(error, 'Could not load that print job.'), true);
    } finally {
      setOpeningJobId(null);
    }
  };

  return (
    <div className="pbl-container">
      <p className="pol-desc">
        Click on a print order and scan the last successful label to handle print errors.
      </p>

      {message && (
        <div className={`pbl-message${isErrorMessage ? ' pbl-message-error' : ''}`}>{message}</div>
      )}

      {loading && entries.length === 0 ? (
        <div className="pbl-loading">
          <div className="pbl-loading-spinner" />
          <div className="pbl-loading-text">Loading print history...</div>
        </div>
      ) : entries.length === 0 ? (
        <div className="pol-empty">No prints recorded yet.</div>
      ) : (
        <>
          <table className="pol-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Date</th>
                <th className="pol-numeric">ID&apos;s</th>
                <th className="pol-numeric">Labels</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr
                  key={row.id}
                  className="pol-row-clickable"
                  tabIndex={0}
                  aria-label={`Open print event handling for ${displayUser(row)}`}
                  onClick={() => {
                    void openPrintEventHandling(row);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void openPrintEventHandling(row);
                    }
                  }}
                >
                  <td className="pol-user">{displayUser(row)}</td>
                  <td className="pol-date">{formatPrintDate(row.createdAt)}</td>
                  <td className="pol-numeric">{row.uniqueIdCount}</td>
                  <td className="pol-numeric">
                    <span className="pol-count">{row.totalLabels}</span>
                    {row.unprintedLabels > 0 && (
                      <button
                        type="button"
                        className="pol-chip"
                        onClick={(event) => {
                          event.stopPropagation();
                          setRetryTarget(row);
                        }}
                        disabled={isRunning}
                        title={
                          row.failureCode
                            ? PRINT_FAILURE_LABELS[row.failureCode]
                            : 'Print the labels that never printed'
                        }
                      >
                        {row.unprintedLabels} not printed
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore && (
            <button
              type="button"
              className="pol-more"
              onClick={() => { void loadPage(offset, true); }}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Show more prints'}
            </button>
          )}
        </>
      )}

      {retryTarget && (
        <div className="pbl-modal-overlay" onClick={() => setRetryTarget(null)}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pbl-modal-close" onClick={() => setRetryTarget(null)}>
              ×
            </button>
            <div className="pbl-modal-title">Reprint remaining labels</div>
            <div className="pbl-manual-popup">
              <p className="pol-confirm-text">
                {retryTarget.unprintedLabels} of {retryTarget.totalLabels} label
                {retryTarget.totalLabels !== 1 ? 's' : ''} from {displayUser(retryTarget)}&apos;s print on{' '}
                {formatPrintDate(retryTarget.createdAt)} never printed
                {retryTarget.failureCode ? ` — ${PRINT_FAILURE_LABELS[retryTarget.failureCode]}` : ''}.
              </p>
              <button
                type="button"
                className="pbl-print-button"
                onClick={() => { void handleRetry(retryTarget); }}
                disabled={isRunning}
              >
                Print {retryTarget.unprintedLabels} Label
                {retryTarget.unprintedLabels !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {handlingJob ? (
        <PrintEventHandlingModal
          key={`${handlingJob.order.id}:${resumedScannedPrefix ?? ''}`}
          isOpen
          onClose={() => setHandlingJob(null)}
          job={handlingJob}
          source="reprint_list"
          printerId={DEFAULT_PRINTER_ID}
          initialScannedPrefix={resumedScannedPrefix}
        />
      ) : null}
    </div>
  );
}

export default PrintOrdersList;
